import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import authService from '../services/authService';
import { isTokenPresent, getAuthHeader } from '../utils/auth';
import fetchUserPermissions from '../services/api';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempUsername, setTempUsername] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [mfaVerifiedForSession, setMfaVerifiedForSession] = useState(false);
  const [loginRetryCount, setLoginRetryCount] = useState(0);
  
  // Branch/Department Context States
  const [currentBranch, setCurrentBranch] = useState(null);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [userBranches, setUserBranches] = useState([]);
  const [userDepartments, setUserDepartments] = useState([]);
  const [isCrossBranch, setIsCrossBranch] = useState(false);
  const [isCrossDepartment, setIsCrossDepartment] = useState(false);
  const [isContextLoaded, setIsContextLoaded] = useState(false);

  const [verifiedOperations, setVerifiedOperations] = useState(() => {
    try {
      const saved = sessionStorage.getItem('mfa_verified_operations');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error parsing verified operations from sessionStorage', e);
      return [];
    }
  });

  const loadBranchDepartmentContext = useCallback(async () => {
    if (loadBranchDepartmentContext._isLoading) {
      console.log('Already loading context, skipping...');
      return;
    }

    try {
      loadBranchDepartmentContext._isLoading = true;
      console.log('Loading branch/department context...');

      if (!user) {
        console.log('No user found, skipping context load');
        setIsContextLoaded(false);
        return;
      }

      console.log('Current user:', user);

      const contextResponse = await authService.getUserContext();
      console.log('Full context response:', contextResponse);

      if (!contextResponse) {
        console.log('No context response received');
        setIsContextLoaded(false);
        return;
      }

      // Set cross-branch/department flags
      setIsCrossBranch(contextResponse.is_cross_branch || false);
      setIsCrossDepartment(contextResponse.is_cross_department || false);

      let branches = contextResponse.accessible_branches_data || [];
      let departments = contextResponse.accessible_departments_data || [];

      if (user.is_superuser || user.is_staff) {
        try {
          // Fetch all branches and departments for superusers
          const [allBranchesResponse, allDepartmentsResponse] = await Promise.all([
            authService.getAllBranches(),
            authService.getAllDepartments()
          ]);
        
          branches = allBranchesResponse.branches || allBranchesResponse || [];
          departments = allDepartmentsResponse.departments || allDepartmentsResponse || [];
        
          // Override the cross-branch/department flags for superusers
          setIsCrossBranch(true);
          setIsCrossDepartment(true);
        
          console.log('Superuser - loaded all branches:', branches);
          console.log('Superuser - loaded all departments:', departments);
        } catch (error) {
          console.error('Failed to fetch all branches/departments for superuser:', error);
          // Fall back to accessible ones
          branches = contextResponse.accessible_branches_data || [];
          departments = contextResponse.accessible_departments_data || [];
        }
      }

      console.log('Branches from API:', branches);
      console.log('Departments from API:', departments);

      const validBranches = branches.filter(b => b && typeof b === 'object' && b.id && b.name);
      const validDepartments = departments.filter(d => d && typeof d === 'object' && d.id && d.name);

      console.log('Valid branches:', validBranches);
      console.log('Valid departments:', validDepartments);

      setAvailableBranches(validBranches);
      setAvailableDepartments(validDepartments);

      // Rest of your existing logic for setting current branch/department...
      let currentBranchData = null;

      const storedBranchId = sessionStorage.getItem('current_branch_id');
      if (storedBranchId && validBranches.length > 0) {
        currentBranchData = validBranches.find(b => b.id.toString() === storedBranchId);
        console.log('Found stored branch:', currentBranchData);
      }

      if (!currentBranchData && contextResponse.branch_id && validBranches.length > 0) {
        currentBranchData = validBranches.find(b => b.id === contextResponse.branch_id);
        console.log('Found branch from API response:', currentBranchData);
      }

      if (!currentBranchData && user.branch_id && validBranches.length > 0) {
        currentBranchData = validBranches.find(b => b.id === user.branch_id);
        console.log('Found branch from user data:', currentBranchData);
      }

      if (!currentBranchData && validBranches.length > 0) {
        currentBranchData = validBranches[0];
        console.log('Using first available branch:', currentBranchData);
      }

      if (!currentBranchData && contextResponse.branch_id && contextResponse.branch_name) {
        currentBranchData = {
          id: contextResponse.branch_id,
          name: contextResponse.branch_name
        };
        console.log('Created branch from context response:', currentBranchData);
      }

      let currentDepartmentData = null;

      const storedDepartmentId = sessionStorage.getItem('current_department_id');
      if (storedDepartmentId && validDepartments.length > 0) {
        currentDepartmentData = validDepartments.find(d => d.id.toString() === storedDepartmentId);
        console.log('Found stored department:', currentDepartmentData);
      }

      if (!currentDepartmentData && contextResponse.department_id && validDepartments.length > 0) {
        currentDepartmentData = validDepartments.find(d => d.id === contextResponse.department_id);
        console.log('Found department from API response:', currentDepartmentData);
      }

      if (!currentDepartmentData && user.department_id && validDepartments.length > 0) {
        currentDepartmentData = validDepartments.find(d => d.id === user.department_id);
        console.log('Found department from user data:', currentDepartmentData);
      }

      if (!currentDepartmentData && validDepartments.length > 0) {
        currentDepartmentData = validDepartments[0];
        console.log('Using first available department:', currentDepartmentData);
      }

      if (!currentDepartmentData && contextResponse.department_id && contextResponse.department_name) {
        currentDepartmentData = {
          id: contextResponse.department_id,
          name: contextResponse.department_name
        };
        console.log('Created department from context response:', currentDepartmentData);
      }

      setCurrentBranch(currentBranchData);
      setCurrentDepartment(currentDepartmentData);

      console.log('Final current branch:', currentBranchData);
      console.log('Final current department:', currentDepartmentData);

      // Store IDs in sessionStorage
      if (currentBranchData?.id) {
        sessionStorage.setItem('current_branch_id', currentBranchData.id.toString());
      } else {
        sessionStorage.removeItem('current_branch_id');
      }

      if (currentDepartmentData?.id) {
        sessionStorage.setItem('current_department_id', currentDepartmentData.id.toString());
      } else {
        sessionStorage.removeItem('current_department_id');
      }

      setIsContextLoaded(true);

    } catch (error) {
      console.error('Failed to load branch/department context:', error);
      console.error('Error details:', error.response?.data || error.message);

      if (!currentBranch && !currentDepartment) {
        setCurrentBranch(null);
        setCurrentDepartment(null);
        setAvailableBranches([]);
        setAvailableDepartments([]);
        setIsCrossBranch(false);
        setIsCrossDepartment(false);
      }
      setIsContextLoaded(false);
    } finally {
      loadBranchDepartmentContext._isLoading = false;
    }
  }, [user?.id]);

  const switchBranch = useCallback(async (branchId) => {
    try {
      if (!isCrossBranch) {
        throw new Error('User does not have cross-branch access');
      }
    
      const branch = availableBranches.find(b => b.id === branchId);
      if (!branch) {
        throw new Error('Branch not found or not accessible');
      }
    
      setCurrentBranch(branch);
      sessionStorage.setItem('current_branch_id', branchId.toString());
    
      // Load departments for new branch with error handling
      let departmentsData = { departments: [], count: 0 };
      try {
        departmentsData = await authService.getBranchDepartments(branchId);
        console.log(`Successfully loaded ${departmentsData.departments.length} departments for branch ${branch.name}`);
      } catch (error) {
        console.warn(`Could not load departments for branch ${branch.name}:`, error.message);
        // Continue with empty departments - don't fail the branch switch
        departmentsData = { departments: [], count: 0 };
      }
    
      setAvailableDepartments(departmentsData.departments || []);
    
      // Clear current department if it doesn't exist in new branch or if no departments exist
      if (currentDepartment) {
        const deptExists = departmentsData.departments?.some(d => d.id === currentDepartment.id);
        if (!deptExists || departmentsData.departments.length === 0) {
          setCurrentDepartment(null);
          sessionStorage.removeItem('current_department_id');
          console.log('Cleared current department - not available in new branch');
        }
      }
    
      // If new branch has departments but no current department is set, optionally set the first one
      if (departmentsData.departments.length > 0 && !currentDepartment) {
        const firstDept = departmentsData.departments[0];
        setCurrentDepartment(firstDept);
        sessionStorage.setItem('current_department_id', firstDept.id.toString());
        console.log(`Auto-selected first department: ${firstDept.name}`);
      }
    
      console.log(`Successfully switched to branch: ${branch.name} (${departmentsData.departments.length} departments available)`);
      return branch;
    
    } catch (error) {
      console.error('Failed to switch branch:', error);
      throw error;
    }
  }, [isCrossBranch, availableBranches, currentDepartment]);

  const switchDepartment = useCallback(async (departmentId) => {
    try {
      if (!isCrossDepartment) {
        throw new Error('User does not have cross-department access');
      }

      const department = availableDepartments.find(d => d.id === departmentId);
      if (!department) {
        throw new Error('Department not found or not accessible');
      }

      setCurrentDepartment(department);
      sessionStorage.setItem('current_department_id', departmentId.toString());

      console.log(`Switched to department: ${department.name}`);
      return department;
    } catch (error) {
      console.error('Failed to switch department:', error);
      throw error;
    }
  }, [isCrossDepartment, availableDepartments]);

  const getCurrentBranchId = useCallback(() => {
    return currentBranch?.id || user?.branch_id || null;
  }, [currentBranch, user]);

  const getCurrentDepartmentId = useCallback(() => {
    return currentDepartment?.id || user?.department_id || null;
  }, [currentDepartment, user]);

  const canAccessBranch = useCallback((branchId) => {
    if (!branchId) return false;
  
    // Superuser can access all branches
    if (user?.is_superuser || user?.is_staff) return true;
  
    // Check if branch is in available branches
    return availableBranches.some(branch => branch.id === branchId);
  }, [user, availableBranches]);

  const canAccessDepartment = useCallback((departmentId) => {
    if (!departmentId) return false;
  
    // Superuser can access all departments
    if (user?.is_superuser || user?.is_staff) return true;
  
    // Check if department is in available departments
    return availableDepartments.some(dept => dept.id === departmentId);
  }, [user, availableDepartments]);

  const getCurrentContext = useCallback(() => {
    return {
      branch: currentBranch,
      department: currentDepartment,
      branch_id: currentBranch?.id || user?.branch_id,
      department_id: currentDepartment?.id || user?.department_id,
      is_cross_branch: isCrossBranch,
      is_cross_department: isCrossDepartment,
      available_branches: availableBranches,
      available_departments: availableDepartments
    };
  }, [currentBranch, currentDepartment, user, isCrossBranch, isCrossDepartment, availableBranches, availableDepartments]);

  // Use the original working hasPermission logic
  const hasPermission = useCallback((requiredPermission, branchId = null, departmentId = null) => {
    console.log(`Checking permission ${requiredPermission}`);
    console.log(`User data:`, user);

    if (!user) {
      console.log('No user found');
      return false;
    }

    // CRITICAL FIX: Simplified and reliable superuser check
    const isSuperuser = 
      user.is_superuser === true || 
      user.is_staff === true;
  
    console.log('Superuser check result:', isSuperuser);
    console.log('Available user fields:', Object.keys(user));

    if (isSuperuser) {
      console.log('Superuser access granted');
      return true;
    }

    // Enhanced roles check (priority system)
    if (user.enhanced_roles && Array.isArray(user.enhanced_roles)) {
      const checkBranchId = branchId || currentBranch?.id || user?.branch_id;
      const checkDepartmentId = departmentId || currentDepartment?.id || user?.department_id;

      const contextRoles = user.enhanced_roles.filter(role => {
        if (role.branch_id && role.branch_id !== checkBranchId) return false;
        if (role.department_id && role.department_id !== checkDepartmentId) return false;
        return true;
      });

      const hasEnhancedPermission = contextRoles.some(role => {
        const permissions = role.permissions || {};

        if (permissions[requiredPermission] === true) return true;
        if (permissions.all === true) return true;
        if (permissions.branch_all === true && !role.department_id) return true;
        if (permissions.department_all === true && role.department_id) return true;

        return false;
      });

      if (hasEnhancedPermission) {
        console.log('Enhanced permission granted');
        return true;
      }
    }

    // CRITICAL FIX: Proper role-based permissions check
    let allPermissions = [];
    if (user.roles && Array.isArray(user.roles)) {
      allPermissions = user.roles.flatMap(role => {
        // Handle both nested permissions in role object and separate permissions array
        const rolePermissions = Array.isArray(role.permissions) ? role.permissions : [];
        console.log(`Role ${role.name} permissions:`, rolePermissions);
        return rolePermissions;
      });
    }

    console.log(`All permissions extracted:`, allPermissions);
    console.log(`Looking for permission: ${requiredPermission}`);

    // Check for exact permission match
    const hasDirectPermission = allPermissions.some(permission => {
      const matches = permission.name === requiredPermission && 
                   (permission.is_active === true || permission.is_active === undefined);
      if (matches) {
        console.log(`Found matching permission:`, permission);
      }
      return matches;
    });

    console.log(`Permission check result: ${hasDirectPermission}`);
    return hasDirectPermission;
  }, [user, currentBranch, currentDepartment]);

  const hasEnhancedPermission = useCallback((permissionKey, branchId = null, departmentId = null) => {
    if (user && user.is_superuser === true) {
      return true;
    }

    const checkBranchId = branchId || currentBranch?.id || user?.branch_id;
    const checkDepartmentId = departmentId || currentDepartment?.id || user?.department_id;

    if (user && user.enhanced_roles && Array.isArray(user.enhanced_roles)) {
      return user.enhanced_roles.some(role => {
        if (role.branch_id && role.branch_id !== checkBranchId) return false;
        if (role.department_id && role.department_id !== checkDepartmentId) return false;

        const permissions = role.permissions || {};
        return permissions[permissionKey] === true ||
               permissions.all === true ||
               (permissions.branch_all === true && !role.department_id) ||
               (permissions.department_all === true && role.department_id);
      });
    }

    return false;
  }, [user, currentBranch, currentDepartment]);

  const hasRole = useCallback((requiredRole, branchId = null, departmentId = null) => {
    if (!user || !user.roles) return false;

    const checkBranchId = branchId || currentBranch?.id || user?.branch_id;
    const checkDepartmentId = departmentId || currentDepartment?.id || user?.department_id;

    return user.roles.some(role => {
      if (role.name === requiredRole) {
        if (role.branch_id && role.branch_id !== checkBranchId) return false;
        if (role.department_id && role.department_id !== checkDepartmentId) return false;
        return true;
      }
      return false;
    });
  }, [user, currentBranch, currentDepartment]);

  const hasEnhancedRole = (roleName) => {
    if (!user || !user.enhanced_roles) return false;
  
    if (Array.isArray(user.enhanced_roles)) {
      return user.enhanced_roles.some(role => {
        const roleNameToCheck = typeof role === 'object' ? role.name : role;
        return roleNameToCheck === roleName;
      });
    }
  
    return false;
  };

  const getFundApprovalLimit = useCallback(() => {
    if (!user) return 0;

    if (user.is_superuser) return Infinity;

    let maxLimit = 0;

    if (user.enhanced_roles && Array.isArray(user.enhanced_roles)) {
      const contextRoles = user.enhanced_roles.filter(role => {
        if (role.branch_id && role.branch_id !== (currentBranch?.id || user?.branch_id)) return false;
        if (role.department_id && role.department_id !== (currentDepartment?.id || user?.department_id)) return false;
        return true;
      });

      contextRoles.forEach(role => {
        const approvalLimits = role.approval_limits || {};
        const roleLimit = approvalLimits.fund_limit || 0;
        if (roleLimit > maxLimit) {
          maxLimit = roleLimit;
        }
      });
    }

    return maxLimit;
  }, [user, currentBranch, currentDepartment]);

  const setToken = (token, rememberMe = false) => {
    if (token) {
      localStorage.setItem('token', token);
      console.log('Token set in localStorage');
    } else {
      localStorage.removeItem('token');
      console.log('Token removed from localStorage');
    }
  };

  const isAuthenticated = useCallback(() => {
    console.log('isAuthenticated called. Token present:', isTokenPresent());
    return isTokenPresent();
  }, []);

  const loadUserPermissions = async () => {
    try {
      console.log('Loading user permissions...');
      setIsLoadingPermissions(true);
      const { permissions } = await fetchUserPermissions();
      setPermissions(permissions);
      console.log('Permissions loaded successfully');
    } catch (error) {
      console.error('Failed to load user permissions:', error);
      setPermissions([]);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      console.log('Fetching user...');
      if (!isTokenPresent()) {
        console.log('No token present, setting states');
        setLoading(false);
        setUser(null);
        setAuthChecked(true);
        setIsInitialized(true);
        setMfaVerifiedForSession(false);
        sessionStorage.removeItem('mfa_verified_session');
        console.log('isInitialized set to true (no token)');
        return;
      }

      try {
        console.log('Token present, fetching current user');
        const currentUser = await authService.getCurrentUser();
        console.log('Current user fetched:', currentUser);
        setUser(currentUser);

        const mfaVerified = sessionStorage.getItem('mfa_verified_session') === 'true';
        setMfaVerifiedForSession(mfaVerified);
        console.log('MFA verified for this session:', mfaVerified);

        await loadUserPermissions();
      } catch (error) {
        console.error('Failed to fetch user:', error);
        setError(error.message || 'An error occurred while fetching user data');
        localStorage.removeItem('token');
        sessionStorage.removeItem('mfa_verified_session');
        setUser(null);
        setMfaVerifiedForSession(false);
      } finally {
        console.log('Setting authChecked to true');
        setLoading(false);
        setIsInitialized(true);
        setAuthChecked(true);
        console.log('isInitialized set to true (after fetch)');
      }
    };

    fetchUser();
  }, []);

  // Load branch/department context after user is loaded
  useEffect(() => {
    if (user && isInitialized && authChecked) {
      console.log('User loaded, loading branch/department context');
      loadBranchDepartmentContext();
    }
  }, [user, isInitialized, authChecked, loadBranchDepartmentContext]);

  const login = async (username, password, rememberMe = false, code = null, backupCode = null) => {
    try {
      console.log('Login attempt initiated with:', {
        username,
        passwordProvided: !!password,
        codeProvided: !!code,
        backupCodeProvided: !!backupCode
      });

      setLoading(true);
      setError(null);

      const is2faAttempt = !!code || !!backupCode;
      console.log('Is this a 2FA verification attempt?', is2faAttempt);

      const response = await authService.login(username, password, code, backupCode);
      console.log('Login response received:', response);

      if (response.requires_2fa || response.requires_mfa) {
        console.log('2FA required, storing credentials temporarily');
        setRequires2FA(true);
        setTempUsername(username);
        setTempPassword(password);
        return { requires2FA: true, data: response };
      }

      console.log('Login successful, storing token and user data');

      if (!response.token) {
        console.error('Login response missing token:', response);
        throw new Error('Invalid response format: missing authentication token');
      }

      setToken(response.token, rememberMe);

      if (!response.user) {
        console.error('Login response missing user data:', response);
        throw new Error('Invalid response format: missing user data');
      }

      if ((is2faAttempt || (response.user && response.user.mfa_enabled)) && response.user.mfa_enabled) {
        setMfaVerifiedForSession(true);
        sessionStorage.setItem('mfa_verified_session', 'true');
        console.log('Setting MFA verified for session to true after successful 2FA login');
      } else {
        setMfaVerifiedForSession(false);
        sessionStorage.removeItem('mfa_verified_session');
        console.log('User does not have MFA enabled, not setting verified flag');
      }

      setUser(response.user);
      setRequires2FA(false);
      setTempUsername('');
      setTempPassword('');
      setLoginRetryCount(0);

      await loadUserPermissions();

      return response;
    } catch (error) {
      console.error('Login failed:', error);

      let errorMessage = 'Login failed';

      if (error.message.includes('verification code')) {
        errorMessage = 'Invalid verification code. Please try again.';
      } else if (error.message.includes('Invalid response format')) {
        errorMessage = 'The server returned an unexpected response. Please try again or contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const complete2FALogin = async (code, useBackupCode = false) => {
    try {
      console.log('Completing 2FA login with:', {
        username: tempUsername,
        passwordStored: !!tempPassword,
        code: useBackupCode ? null : code,
        backupCode: useBackupCode ? code : null
      });

      if (!tempUsername || !tempPassword) {
        const errorMsg = 'Missing credentials for 2FA completion';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      setLoginRetryCount(prevCount => prevCount + 1);

      if (loginRetryCount > 3) {
        throw new Error('Too many login attempts. Please refresh the page and try again.');
      }

      try {
        const response = await authService.login(
          tempUsername,
          tempPassword,
          useBackupCode ? null : code,
          useBackupCode ? code : null
        );

        console.log('2FA completion response:', response);

        if (response.requires_2fa || response.requires_mfa) {
          throw new Error('Invalid verification code. Please try again.');
        }

        if (response.token) {
          setToken(response.token);

          setMfaVerifiedForSession(true);
          sessionStorage.setItem('mfa_verified_session', 'true');
          console.log('MFA session verified via 2FA login, setting session flag');

          setUser(response.user);

          setRequires2FA(false);
          setTempUsername('');
          setTempPassword('');
          setLoginRetryCount(0);

          await loadUserPermissions();
          return response;
        }

        console.error('Invalid response format after 2FA completion:', response);
        throw new Error('Login process completed but returned an unexpected format. Please try again or contact support.');
      } catch (error) {
        if (error.response && error.response.status === 400 && loginRetryCount <= 1) {
          console.log('Session error detected, retrying with delay...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return complete2FALogin(code, useBackupCode);
        }
        throw error;
      }
    } catch (error) {
      console.error('2FA completion failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('Logging out...');
      await authService.logout();
      setUser(null);
      setMfaVerifiedForSession(false);
      setVerifiedOperations([]);
      
      // Clear branch/department context
      setCurrentBranch(null);
      setCurrentDepartment(null);
      setAvailableBranches([]);
      setAvailableDepartments([]);
      setIsCrossBranch(false);
      setIsCrossDepartment(false);
      
      sessionStorage.removeItem('mfa_verified_session');
      sessionStorage.removeItem('mfa_verified_operations');
      sessionStorage.removeItem('current_branch_id');
      sessionStorage.removeItem('current_department_id');
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('token');
      sessionStorage.removeItem('mfa_verified_session');
      sessionStorage.removeItem('mfa_verified_operations');
      sessionStorage.removeItem('current_branch_id');
      sessionStorage.removeItem('current_department_id');
      setUser(null);
      setMfaVerifiedForSession(false);
      setVerifiedOperations([]);
      setCurrentBranch(null);
      setCurrentDepartment(null);
      setAvailableBranches([]);
      setAvailableDepartments([]);
      setIsCrossBranch(false);
      setIsCrossDepartment(false);
    }
  };

  const isOperationMfaVerified = useCallback((operationType) => {
    const normalizedOperation = operationType.toUpperCase();
    console.log('Checking if operation is MFA verified:', normalizedOperation);
    console.log('Current verified operations:', verifiedOperations);

    if (verifiedOperations.includes('FUND_MANAGEMENT')) {
      console.log('FUND_MANAGEMENT is verified, all fund operations are verified');
      return true;
    }

    return verifiedOperations.some(op => op.toUpperCase() === normalizedOperation);
  }, [verifiedOperations]);

  const markOperationAsVerified = useCallback((operationType) => {
    const normalizedOperation = operationType.toUpperCase();
    console.log('Marking operation as verified:', normalizedOperation);

    setVerifiedOperations(prev => {
      const isAlreadyVerified = prev.some(op => op.toUpperCase() === normalizedOperation);
      if (isAlreadyVerified) {
        return prev;
      }

      const updated = [...prev, normalizedOperation];
      sessionStorage.setItem('mfa_verified_operations', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const verifyMfaForOperation = async (code, operationType = 'FUND_MANAGEMENT', useBackupCode = false) => {
    try {
      const normalizedOperation = operationType.toUpperCase();
      console.log('Verifying MFA for operation:', normalizedOperation);
      const response = await authService.verifyMfaSession(code, normalizedOperation, useBackupCode);

      markOperationAsVerified(normalizedOperation);
      setMfaVerifiedForSession(true);
      sessionStorage.setItem('mfa_verified_session', 'true');
      console.log('MFA session verified successfully, setting mfaVerifiedForSession to true');

      return response;
    } catch (error) {
      console.error('MFA verification for operation failed:', error);
      throw error;
    }
  };

  const generateMfaChallenge = async (operationType = 'FUND_MANAGEMENT') => {
    try {
      const normalizedOperation = operationType.toUpperCase();
      console.log('Generating MFA challenge for operation:', normalizedOperation);

      if (!user || !user.id) {
        await authService.getCurrentUser();
      }

      return await authService.generateMfaChallenge(normalizedOperation, 'totp', user?.id);
    } catch (error) {
      console.error('Failed to generate MFA challenge:', error);
      throw error;
    }
  };

  const setupMFA = async (password, operationType = 'FUND_MANAGEMENT') => {
    try {
      const normalizedOperation = operationType.toUpperCase();
      console.log(`Setting up MFA for operation: ${normalizedOperation}`);
      const response = await authService.setupMFA(password, normalizedOperation);

      if (response) {
        console.log('MFA setup initiated successfully');
        return response;
      }
    } catch (error) {
      console.error('Failed to set up MFA:', error);
      throw error;
    }
  };

  const setup2FA = async (password) => {
    try {
      return await authService.setup2FA(password);
    } catch (error) {
      console.error('Setup 2FA failed:', error);
      throw error;
    }
  };

  const verify2FA = async (code) => {
    try {
      const response = await authService.verify2FA(code);

      setUser(prev => ({
        ...prev,
        two_factor_enabled: true,
        mfa_enabled: true
      }));

      setMfaVerifiedForSession(true);
      sessionStorage.setItem('mfa_verified_session', 'true');

      return response;
    } catch (error) {
      console.error('Verify 2FA failed:', error);
      throw error;
    }
  };

  const disable2FA = async (password) => {
    try {
      const response = await authService.disable2FA(password);

      setUser(prev => ({
        ...prev,
        two_factor_enabled: false,
        mfa_enabled: false
      }));

      return response;
    } catch (error) {
      console.error('Disable 2FA failed:', error);
      throw error;
    }
  };

  const regenerateBackupCodes = async (password) => {
    try {
      return await authService.regenerateBackupCodes(password);
    } catch (error) {
      console.error('Regenerate backup codes failed:', error);
      throw error;
    }
  };

  const isMfaEnabled = () => {
    return user && user.mfa_enabled === true;
  };

  const is2faEnabledOnly = () => {
    return user && user.two_factor_enabled === true && user.mfa_enabled !== true;
  };

  const value = {
    user,
    loading,
    error,
    isInitialized,
    authChecked,
    permissions,
    isLoadingPermissions,
    requires2FA,
    mfaVerifiedForSession,
    verifiedOperations,

    // Branch/Department Context
    currentBranch,
    currentDepartment,
    availableBranches,
    availableDepartments,
    userBranches,
    userDepartments,
    isCrossBranch,
    isCrossDepartment,
    getCurrentBranchId,
    getCurrentDepartmentId,
    canAccessBranch,
    canAccessDepartment,
    isContextLoaded,

    login,
    complete2FALogin,
    logout,
    isAuthenticated,
    hasPermission,
    hasEnhancedPermission,
    hasRole,
    hasEnhancedRole,
    getCurrentContext,
    switchBranch,
    switchDepartment,
    getFundLimits: getFundApprovalLimit,
    loadBranchDepartmentContext,

    isOperationMfaVerified,
    markOperationAsVerified,
    verifyMfaForOperation,
    generateMfaChallenge,
    setupMFA,
    setup2FA,
    verify2FA,
    disable2FA,
    regenerateBackupCodes,
    isMfaEnabled,
    is2faEnabledOnly,
    isMfaVerified: () => mfaVerifiedForSession || (user && user.mfa_enabled === false),
    setUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
